/**
 * Public App Routes
 *
 * Handles all public-facing pages:
 * - / (Landing Page)
 * - /legal/* (Privacy and Terms)
 *
 * Uses shadcn/ui + Tailwind CSS for styling.
 *
 * Note: Demo mode (/demo/*) is handled by a separate entry point (demo.html)
 * with Polaris Web Components for consistent UI with the embedded app.
 */

import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Landing from "../pages/Landing";
import i18nInstance from "../i18n/config";
import routeMetadata from "../content/public-route-meta.json";

// Public legal pages
const PrivacyPolicy = lazy(() => import("../pages/Legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("../pages/Legal/TermsOfService"));

/**
 * Tailwind-styled loading fallback
 */
const LoadingFallback = (
  <div
    className="flex items-center justify-center min-h-screen bg-background"
    role="status"
    aria-live="polite"
  >
    <div className="text-center space-y-4">
      <div
        aria-hidden
        className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
      />
      <p className="text-sm text-muted-foreground">Loading page…</p>
    </div>
  </div>
);

const BASE_URL = "https://app.ai-commerce-ready.com";

const ROUTE_META = routeMetadata;

type RouteMeta = (typeof ROUTE_META)[keyof typeof ROUTE_META];

const DEFAULT_META = ROUTE_META["/"];

const getRouteMeta = (path: string): RouteMeta => {
  if (path.startsWith("/legal/privacy")) return ROUTE_META["/legal/privacy"];
  if (path.startsWith("/legal/terms")) return ROUTE_META["/legal/terms"];
  return DEFAULT_META;
};

const updateMetaContent = (selector: string, content: string) => {
  const element = document.querySelector<HTMLMetaElement>(selector);
  if (element) {
    element.setAttribute("content", content);
  }
};

const updateLinkHref = (selector: string, href: string) => {
  const element = document.querySelector<HTMLLinkElement>(selector);
  if (element) {
    element.setAttribute("href", href);
  }
};

const updateDocumentMeta = (path: string) => {
  const meta = getRouteMeta(path);
  const url = `${BASE_URL}${meta.path}`;

  document.title = meta.title;
  updateMetaContent('meta[name="title"]', meta.title);
  updateMetaContent('meta[name="description"]', meta.description);
  updateMetaContent('meta[property="og:title"]', meta.title);
  updateMetaContent('meta[property="og:description"]', meta.description);
  updateMetaContent('meta[property="og:url"]', url);
  updateMetaContent('meta[name="twitter:title"]', meta.title);
  updateMetaContent('meta[name="twitter:description"]', meta.description);
  updateMetaContent('meta[name="twitter:url"]', url);
  updateLinkHref('link[rel="canonical"]', url);
};

function PublicApp({ preferredLanguage }: { readonly preferredLanguage?: string }) {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const path = location.pathname;

  // Restore the browser preference only after React commits the prerendered English tree.
  useEffect(() => {
    if (preferredLanguage) void i18nInstance.changeLanguage(preferredLanguage);
  }, [preferredLanguage]);

  useEffect(() => {
    if (path.startsWith("/demo")) return;
    const normalizedPath = path === "/" ? "/" : path.replace(/\/$/, "");
    if (normalizedPath === "/") {
      const localizedMeta = {
        ...ROUTE_META["/"],
        title: t("landing:meta.title"),
        description: t("landing:meta.description"),
      };
      const url = `${BASE_URL}/`;
      document.title = localizedMeta.title;
      updateMetaContent('meta[name="title"]', localizedMeta.title);
      updateMetaContent('meta[name="description"]', localizedMeta.description);
      updateMetaContent('meta[property="og:title"]', localizedMeta.title);
      updateMetaContent('meta[property="og:description"]', localizedMeta.description);
      updateMetaContent('meta[name="twitter:title"]', localizedMeta.title);
      updateMetaContent('meta[name="twitter:description"]', localizedMeta.description);
      updateMetaContent('meta[property="og:url"]', url);
      updateMetaContent('meta[name="twitter:url"]', url);
      updateLinkHref('link[rel="canonical"]', url);
    } else {
      updateDocumentMeta(normalizedPath);
    }
  }, [i18n.resolvedLanguage, path, t]);

  // Redirect /demo/* to demo.html (separate Polaris entry point)
  // This handles SPA navigation from Landing page to Demo
  useEffect(() => {
    if (path.startsWith("/demo")) {
      window.location.href = path;
    }
  }, [path]);

  // Show loading while redirecting to demo
  if (path.startsWith("/demo")) {
    return LoadingFallback;
  }

  // Legal pages (/legal/*)
  if (path.startsWith("/legal")) {
    return (
      <Suspense fallback={LoadingFallback}>
        <Routes>
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route
            path="/legal/*"
            element={<Navigate to="/legal/privacy" replace />}
          />
        </Routes>
      </Suspense>
    );
  }

  // Keep the primary landing route in the initial bundle so the Hero never
  // waits behind a full-screen lazy boundary. Legal routes remain lazy.
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default PublicApp;
